/**
 * Hospital People & Shifts — Common constants, types, and validation.
 * R39 C6.a1 (plan docs/plans/sector-operations.md §2 SO-P252, H§4).
 */

export const HOSPITAL_STAFF_GROUPS = [
  'DOCTOR',
  'NURSE',
  'SUPPORT',
  'OTHER',
] as const

export type HospitalStaffGroup = (typeof HOSPITAL_STAFF_GROUPS)[number]
export type OpsStaffGroup = HospitalStaffGroup

export const HOSPITAL_CREDENTIAL_TAGS = [
  'BLS',
  'ACLS',
  'PALS',
  'NRP',
  'ICU',
  'NICU',
  'OT_SCRUB',
  'OT_CIRCULATING',
  'DIALYSIS',
  'TRIAGE',
] as const

export const HOSPITAL_PRIVILEGE_TAGS = [
  'PRIV_ANAESTHESIA',
  'PRIV_EMERGENCY',
  'PRIV_OBSTETRICS',
  'PRIV_PAEDIATRICS',
  'PRIV_GENERAL_SURGERY',
] as const

export const HOSPITAL_SKILL_TAGS = [
  ...HOSPITAL_CREDENTIAL_TAGS,
  ...HOSPITAL_PRIVILEGE_TAGS,
] as const

export type HospitalCredentialTag = (typeof HOSPITAL_CREDENTIAL_TAGS)[number]
export type HospitalPrivilegeTag = (typeof HOSPITAL_PRIVILEGE_TAGS)[number]
export type HospitalSkillTag = (typeof HOSPITAL_SKILL_TAGS)[number]

export function isValidStaffGroup(val: unknown): val is HospitalStaffGroup {
  return typeof val === 'string' && HOSPITAL_STAFF_GROUPS.includes(val as HospitalStaffGroup)
}

export function isValidHospitalSkillTag(val: unknown): val is HospitalSkillTag {
  return typeof val === 'string' && HOSPITAL_SKILL_TAGS.includes(val as HospitalSkillTag)
}

export interface SkillMixRuleResult {
  ruleId: string
  placeId?: string | null
  shiftId: string
  dutyRoleKey?: string | null
  staffGroup?: OpsStaffGroup
  requiredSkill?: string | null
  minCount: number
  actualCount: number
  shortfall: number
  isShort: boolean
  assignedPersonKeys: string[]
}

export interface SkillMixMatrixOptions {
  coverageRules: Array<{
    id: string
    placeId?: string | null
    shiftId: string
    dutyRoleKey?: string | null
    minCount: number
    requiredSkill?: string | null
    staffGroup?: OpsStaffGroup
    archived?: boolean
  }>
  assignments: Array<{
    id?: string
    placeId?: string | null
    shiftId: string
    dutyRoleKey?: string | null
    dayKey: string
    personRef: { kind: 'MEMBER'; uid: string } | { kind: 'WORKER'; workerId?: string }
    kind?: string
  }>
  personStaffGroups?: Record<string, OpsStaffGroup>
  personSkills?: Record<string, string[]>
  placeId?: string
  dayKey?: string
}

function resolvePersonKey(ref: { kind: 'MEMBER'; uid: string } | { kind: 'WORKER'; workerId?: string }): string {
  return ref.kind === 'MEMBER' ? `m_${ref.uid}` : `w_${ref.workerId || ''}`
}

export function computeSkillMixMatrix(options: SkillMixMatrixOptions): SkillMixRuleResult[] {
  const {
    coverageRules,
    assignments,
    personStaffGroups = {},
    personSkills = {},
    placeId,
    dayKey,
  } = options

  const filteredRules = coverageRules.filter((r) => {
    if (r.archived) return false
    if (placeId && r.placeId && r.placeId !== placeId) return false
    return true
  })

  const filteredAssignments = assignments.filter((a) => {
    if (a.kind && a.kind !== 'STANDARD') return false
    if (placeId && a.placeId && a.placeId !== placeId) return false
    if (dayKey && a.dayKey !== dayKey) return false
    return true
  })

  return filteredRules.map((rule) => {
    const matchingAssignments = filteredAssignments.filter((a) => {
      if (a.shiftId !== rule.shiftId) return false
      if (rule.placeId && a.placeId !== rule.placeId) return false
      if (rule.dutyRoleKey && a.dutyRoleKey !== rule.dutyRoleKey) return false

      const pKey = resolvePersonKey(a.personRef)
      if (rule.staffGroup) {
        const group = personStaffGroups[pKey]
        if (group !== rule.staffGroup) return false
      }

      if (rule.requiredSkill) {
        const skills = personSkills[pKey] || []
        if (!skills.includes(rule.requiredSkill)) return false
      }

      return true
    })

    const assignedPersonKeys = matchingAssignments.map((a) => resolvePersonKey(a.personRef))
    const actualCount = assignedPersonKeys.length
    const minCount = rule.minCount || 0
    const shortfall = Math.max(0, minCount - actualCount)

    return {
      ruleId: rule.id,
      placeId: rule.placeId,
      shiftId: rule.shiftId,
      dutyRoleKey: rule.dutyRoleKey,
      staffGroup: rule.staffGroup,
      requiredSkill: rule.requiredSkill,
      minCount,
      actualCount,
      shortfall,
      isShort: actualCount < minCount,
      assignedPersonKeys,
    }
  })
}

